/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { fetchTraceGraphByPurchaseOrderId } from '@/api/orders';
import { OrderTracingTab } from '@/app/(client)/orders/-components/OrderTracingTab';
import { useEntityScreeningMatches } from '@/app/(client)/orders/-hooks/useEntityScreeningMatches';
import { useTraceGraphByPurchaseOrderId } from '@/app/(client)/orders/-hooks/useTraceGraphByPurchaseOrderId';
import { traceGraphKeys } from '@/app/(client)/orders/-hooks/useTraceGraphBySalesOrderId';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatures } from '@/hooks/useFeatures';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/purchase-orders/$poId/tracing',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: traceGraphKeys.detail(params.poId),
      queryFn: () =>
        fetchTraceGraphByPurchaseOrderId(params.poId).then(res => res.data),
    }),
  component: PurchaseOrderTracingRoute,
});

function PurchaseOrderTracingRoute() {
  const { poId } = Route.useParams();
  const { isTracingAiEnabled, isEntityScreeningV2Enabled, isKharonV2Enabled } =
    useFeatures();

  const { data: traceGraph, isLoading } = useTraceGraphByPurchaseOrderId(poId);

  const companyIds = useMemo(
    () =>
      traceGraph?.layers?.flatMap(
        layer => layer.nodes?.map(node => node.assetId) ?? [],
      ) ?? [],
    [traceGraph],
  );

  const { data: screeningMatches } = useEntityScreeningMatches(companyIds, {
    enabled: isEntityScreeningV2Enabled && isKharonV2Enabled,
  });

  if (isLoading) {
    return (
      <div className='flex flex-col gap-4'>
        <Skeleton className='h-8 w-40' />
        <Skeleton className='w-full rounded-xl h-[calc(100vh-340px)] min-h-100' />
      </div>
    );
  }

  if (!traceGraph) return null;

  return (
    <OrderTracingTab
      orderId={poId}
      traceGraph={traceGraph}
      entityScreeningMatches={screeningMatches ?? []}
      isTracingAiEnabled={isTracingAiEnabled}
    />
  );
}
